import i from 'i18next'
import useTranslations, { getTranslations } from 'lib/hooks/use-translations'
import { getMenu, getMenuCollections } from 'lib/hooks/use-collections'
import Layout from 'components/Layout'
import Title from 'components/Title'
import Footer from 'components/sections/Footer'
import Reviews, { Props } from 'components/sections/Reviews'
import { getProductInfo, getProducts, getWeightedAverage } from 'lib/hooks/use-products'
import { H1 } from 'components/sections/Header'
import { motion } from 'framer-motion'
import Products from 'components/sections/Products'
import TextImageHalf from 'components/sections/TextImageHalf'
import { headers as ease } from 'constants/easings'
import Image from 'components/Image'

export async function getStaticProps() {
	const translations = await getTranslations({ page: 'anti-aging' })
	const nav = await getMenu()
	const collections = await getMenuCollections()
	const collection = await getProducts( 'anti-aging' )
	const products = ( await getProductInfo() ).filter( ({ title }) => /aging/i.test( title ) )

	const { num, average, stars } = getWeightedAverage( products )

	return {
		props: { translations, nav, collections, collection, products, num, average, stars },
		revalidate: 60 * 30,
	}
}

export default function AntiAgingPage({ loaded, translations, nav, collections, collection, products, num, average, stars }) {
	useTranslations( translations )

	const handles: Props['handles'] = new Map()
	products.forEach( product => handles.set( product.handle, product ) )

	return (
		<Layout loaded={loaded} nav={nav} collections={collections}>
			<Title page="anti-aging" />
			<header className="container">
				<div>
					<H1 title={i.t( 'anti-aging.hero' )} lines={3} />
				</div>
				<motion.picture
					className="image-1"
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ duration: 1.5, delay: .25, ease }}
				>
					<Image
						src={i.t( 'anti-aging.images.1' )}
						sizes={`( max-width: 1300px ) 100vw, ${Math.ceil( 46 * 1.15 )}vw`}
						loading="lazy"
						style={{ height: '115%' }}
						initial={{ y: 0 }}
						animate={{ y: '-10%' }}
						transition={{ duration: 1.5, delay: .15, ease }}
					/>
				</motion.picture>
				<style jsx>{`
					header {
						display: grid;
						grid-template-columns: 1fr 1fr;
						gap: 100px;
						align-items: center;
					}
					@media ( max-width: 600px ) {
						header {
							grid-template-columns: 1fr;
						}
					}
					header :global(h1) {
						margin-top: 0;
					}
					header :global(.image-1) {
						position: relative;
						height: 60vh;
					}
					header :global(.image-1 img) {
						position: absolute;
					}
				`}</style>
			</header>

			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ delay: 1 }}
			>
				<Products products={collection} />
				<section className="container" style={{ textAlign: 'center' }}>
					<iframe
						src="https://www.youtube.com/embed/rxXTClr1CgE?si=XOpTgJUy4CvIivBk&controls=0&rel=0"
						title="YouTube video player"
						frameBorder="0"
						allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
						allowFullScreen
						style={{ width: '100%', aspectRatio: '16 / 9' }}
					></iframe>
				</section>
				<TextImageHalf
					align="right"
					image={i.t( 'anti-aging.intro.image' )}
					text={i.t( 'anti-aging.intro', { postProcess: 'markdown-jsx' })}
				/>
				<TextImageHalf
					align="left"
					image={i.t( 'anti-aging.ingredients.image' )}
					text={i.t( 'anti-aging.ingredients', { postProcess: 'markdown-jsx' })}
				/>
				<TextImageHalf
					align="right"
					image={i.t( 'anti-aging.testpanel.image' )}
					text={i.t( 'anti-aging.testpanel', { postProcess: 'markdown-jsx' })}
				/>

				<Reviews
					page={3}
					title={i.t( 'reviews.title' )}
					average={average}
					num={num}
					stars={stars}
					handle={Array.from( handles.keys() )}
					handles={handles}
				/>

				<section className="container">
					<a
						className="button large"
						style={{ width: 'max-content', marginInline: 'auto' }}
						href="/products/anti-aging"
					>
						{i.t( 'anti-aging.cta' )}
					</a>
				</section>
			</motion.div>
			<Footer />
		</Layout>
	)
}
